import { Method } from 'axios';
import { getOpenApiOperationParameters } from '../../../common/src/routes/getOpenApiOperationParameters';
import {
    RequestBodyWithUndefined,
    Response200,
    ZxyOpenApiMethodsKeys,
    ZxyOpenApiPathAndOperationParameter,
    ZxyOpenApiPathsKeys,
} from '../../../common/src/routes/openApiTypes';
import { assembleUrl } from './assembleUrl';
import { fetchJsonWithThrow } from './fetch';

export class OpenApiFetchError extends Error {
    constructor(
        public statusCode: number,
        public errorCode: string | undefined,
        public innerError: Error | string | undefined,
        public url: string,
        public method: string,
    ) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        super(`${statusCode} ${errorCode} ${innerError ?? ''} ${url} ${method}`);
        this.name = 'OpenApiFetchError';
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.stack = innerError && (innerError as any).stack ? (innerError as any).stack : (<any>new Error()).stack;
        this.statusCode = statusCode;
        this.errorCode = errorCode;
        this.innerError = innerError;
    }
}

export async function openApiFetchPost<
    Path extends ZxyOpenApiPathsKeys,
    OpenApiMethod extends ZxyOpenApiMethodsKeys<Path> & 'post',
    PathMethodParameters extends ZxyOpenApiPathAndOperationParameter<Path, OpenApiMethod>,
>(path: Path, parameters: PathMethodParameters, body: RequestBodyWithUndefined<Path, OpenApiMethod>): Promise<Response200<Path, OpenApiMethod>> {
    return openApiFetch(path, 'post' as unknown as OpenApiMethod, parameters, body);
}

export async function openApiFetchPut<
    Path extends ZxyOpenApiPathsKeys,
    OpenApiMethod extends ZxyOpenApiMethodsKeys<Path> & 'put',
    PathMethodParameters extends ZxyOpenApiPathAndOperationParameter<Path, OpenApiMethod>,
>(path: Path, parameters: PathMethodParameters, body: RequestBodyWithUndefined<Path, OpenApiMethod>): Promise<Response200<Path, OpenApiMethod>> {
    return openApiFetch(path, 'put' as unknown as OpenApiMethod, parameters, body);
}

export async function openApiFetchGet<
    Path extends ZxyOpenApiPathsKeys,
    OpenApiMethod extends ZxyOpenApiMethodsKeys<Path> & 'get',
    PathMethodParameters extends ZxyOpenApiPathAndOperationParameter<Path, OpenApiMethod>,
>(path: Path, parameters: PathMethodParameters): Promise<Response200<Path, OpenApiMethod>> {
    return openApiFetch(path, 'get' as unknown as OpenApiMethod, parameters, undefined as unknown as RequestBodyWithUndefined<Path, OpenApiMethod>);
}

export async function openApiFetchDelete<
    Path extends ZxyOpenApiPathsKeys,
    OpenApiMethod extends ZxyOpenApiMethodsKeys<Path> & 'delete',
    PathMethodParameters extends ZxyOpenApiPathAndOperationParameter<Path, OpenApiMethod>,
>(path: Path, parameters: PathMethodParameters): Promise<Response200<Path, OpenApiMethod>> {
    return openApiFetch(
        path,
        'delete' as unknown as OpenApiMethod,
        parameters,
        undefined as unknown as RequestBodyWithUndefined<Path, OpenApiMethod>,
    );
}

export function assembleOpenApiUrl<
    Path extends ZxyOpenApiPathsKeys,
    OpenApiMethod extends ZxyOpenApiMethodsKeys<Path>,
    PathMethodParameters extends ZxyOpenApiPathAndOperationParameter<Path, OpenApiMethod>,
>(path: Path, method: OpenApiMethod, inputParameters: PathMethodParameters): string {
    const parameters = getOpenApiOperationParameters(path, method as string);

    let url = path as string;

    const pathParameters = parameters.filter((parameter) => parameter.in === 'path');

    for (const pathParameter of pathParameters) {
        const parameterName = pathParameter.name;
        const parameterValue = (inputParameters as Record<string, string>)[parameterName];
        if (parameterValue === undefined) {
            throw new Error(`Missing path parameter ${parameterName} for ${path} ${method as string}`);
        }
        url = url.replace(`{${parameterName}}`, parameterValue);
    }

    const queryParametersObject: Record<string, string> = {};
    const queryParametersParameters = parameters.filter((parameter) => parameter.in === 'query');
    for (const queryParameter of queryParametersParameters) {
        const parameterName = queryParameter.name;
        const parameterValue = (inputParameters as Record<string, string>)[parameterName];
        if (parameterValue === undefined) {
            if (queryParameter.required) {
                throw new Error(`Missing query parameter ${parameterName} for ${path} ${method as string}`);
            }
        } else {
            queryParametersObject[parameterName] = parameterValue;
        }
    }

    return assembleUrl(url, queryParametersObject);
}

export async function openApiFetch<
    Path extends ZxyOpenApiPathsKeys,
    OpenApiMethod extends ZxyOpenApiMethodsKeys<Path>,
    PathMethodParameters extends ZxyOpenApiPathAndOperationParameter<Path, OpenApiMethod>,
>(
    path: Path,
    method: OpenApiMethod,
    parameters: PathMethodParameters,
    body: RequestBodyWithUndefined<Path, OpenApiMethod>,
): Promise<Response200<Path, OpenApiMethod>> {
    let url = path as string;
    const queryParameters = {} as Record<string, string | number | boolean | undefined>;
    for (const parameterKey of Object.keys(parameters)) {
        if (url.includes(`{${parameterKey}}`)) {
            const parameterValue = (parameters as any)[parameterKey]; // TODO
            url = url.replace(`{${parameterKey}}`, parameterValue);
        } else {
            queryParameters[parameterKey] = (parameters as any)[parameterKey]; // TODO
        }
    }

    url = assembleUrl(url, queryParameters);
    const result = await fetchJsonWithThrow(url, method as Method, body as any);

    return result.json;
}
